import React from "react";
import { Link } from "react-router-dom";
import MarketingPageLayout, { PolicySection } from "@/components/marketing/MarketingPageLayout";

export default function IncidentReviewPolicy() {
  return (
    <MarketingPageLayout
      eyebrow="Compliance"
      title="Incident Review Policy"
      subtitle="How GuestIntel reviews, verifies, and admits incident records into the network."
      lastUpdated="February 2026"
      path="/incident-review"
      seoDescription="How GuestIntel reviews, verifies, and admits incident records into the network — submission requirements, review tiers, timelines, and severity calibration."
    >
      <PolicySection title="1. Purpose">
        <p>
          Every record on the network has to clear a documented review process. This policy describes who
          reviews submissions, on what criteria, with what evidence, and how long review takes.
        </p>
      </PolicySection>

      <PolicySection title="2. Submission requirements">
        <ul>
          <li>Incident category, description, date, and severity.</li>
          <li>At least one piece of supporting evidence — photo, invoice, police report, or signed statement.</li>
          <li>Guest identifiers as available — name, ID type and number, contact details. Direct identifiers are encrypted at rest.</li>
          <li>The submitting operator's name, role, and timestamp.</li>
        </ul>
      </PolicySection>

      <PolicySection title="3. Review tiers">
        <ul>
          <li><strong>Tier 1 — Automated checks.</strong> Schema validation, duplicate detection, hash-collision triage.</li>
          <li><strong>Tier 2 — Case manager review.</strong> A trained GuestIntel case manager validates the evidence, severity classification, and policy fit.</li>
          <li><strong>Tier 3 — Independent escalation.</strong> Contentious or high-impact submissions go to our review board (rotating panel of independent hospitality professionals).</li>
        </ul>
      </PolicySection>

      <PolicySection title="4. Review timelines">
        <p>
          Tier 1 is automated and instant. Tier 2 targets &le; 72 hours (often same business day). Tier 3
          targets &le; 10 business days. Records remain visible to the owning property during review;
          cross-tenant visibility is gated on Tier 2 approval.
        </p>
      </PolicySection>

      <PolicySection title="5. Severity calibration">
        <p>
          Severity is normalised to a 1–4 scale (low → critical). Reviewers consult a published rubric:
          financial impact, repeatability, safety risk, and evidence strength. See{" "}
          <Link to="/compliance-framework">Compliance Framework</Link> for the full rubric.
        </p>
      </PolicySection>

      <PolicySection title="6. Rejection & feedback">
        <p>
          Submissions that fail review are returned to the operator with a written reason and an
          opportunity to resubmit with additional evidence. Two unsuccessful resubmissions within 90 days
          trigger a tenant-level conversation about evidence standards.
        </p>
      </PolicySection>

      <PolicySection title="7. Appeals">
        <p>
          Any guest may appeal a record about them via the <Link to="/appeal">Appeal portal</Link>. See the{" "}
          <Link to="/dispute-resolution">Dispute Resolution Policy</Link> for the full process.
        </p>
      </PolicySection>
    </MarketingPageLayout>
  );
}
